import { AiImagePresetStyles, AiMessage, GenerationFile } from "common/aiConstants";
import { IPresentation, IPresentationStorageModel, ISlide, IStorageModel } from "common/interfaces";

import { AmplitudeCategories, trackEvent } from "./amplitude";

export function GeneratePresentation({ presentation: _presentation }: { presentation: IPresentation | IPresentationStorageModel }) {
    const presentation: IPresentation = ("get" in _presentation) ? _presentation.attributes : _presentation;

    const metadata = presentation.metadata ?? {};

    const isPresetImageStyle = AiImagePresetStyles.some(style => style.style === metadata.imageStyle);

    const props = {
        ..._getFilesProps(metadata.files ?? []),
        presentation_id: presentation.id,
        num_slides: Object.keys(presentation.slideRefs).length,
        from_tour: false,
        used_ai_images: metadata.imageSource === "ai",
        model_name: metadata.imageModel,
        image_quality: metadata.imageQuality,
        custom_style_type: isPresetImageStyle ? "preset" : "prompt",
        custom_style_type_prompt: isPresetImageStyle ? null : metadata.imageStyle,
        image_style: isPresetImageStyle ? metadata.imageStyle : "custom",
        language: metadata.language,
        prompt: metadata.prompt,
        preserve_exact_text: metadata.preserveExactText,
        web_search: metadata.webSearch
    };

    trackEvent(AmplitudeCategories.Presentation, "Generate", props);
}

function _getFilesProps(files: GenerationFile[]) {
    const usedFiles = files.filter(file => file.hasBeenUsed);
    return {
        files: usedFiles.map(({ rawFileName, parsedFileName, hasBeenUsedFor }) => ({ rawFileName, parsedFileName, hasBeenUsedFor })),
        files_count: usedFiles.length
    };
}

export function GeneratePresentationOutline({ prompt, files, webSearch }: { prompt: string, files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "Generate", props);
}

export function RegeneratePresentationOutline({ prompt, files, webSearch }: { prompt: string, files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "Regenerate", props);
}

export function ChatEditPresentationOutline({ prompt, messages, files, webSearch }: { prompt: string, messages: AiMessage[], files: GenerationFile[], webSearch: boolean }) {
    const props = {
        ..._getFilesProps(files),
        prompt: prompt,
        messages: messages.map(({ role, content }) => ({ role, content })),
        web_search: webSearch
    };

    trackEvent(AmplitudeCategories.PresentationOutline, "ChatEdit", props);
}

function _getSlide(_slide: ISlide | IStorageModel<ISlide>) {
    const slide: ISlide = ("get" in _slide) ? _slide.attributes : _slide;
    return slide;
}

function _getSlideProps(_slide: ISlide | IStorageModel<ISlide>) {
    const slide = _getSlide(_slide);

    const metadata = slide.metadata ?? {};

    const imageSource = metadata.imageSource ?? metadata.generatedModel?.slideImage?.imageSource;
    const imageStyle = metadata.imageStyle ?? metadata.generatedModel?.slideImage?.imageStyle;
    const imageModel = metadata.imageModel ?? metadata.generatedModel?.slideImage?.imageModel;
    const imageQuality = metadata.imageQuality ?? metadata.generatedModel?.slideImage?.imageQuality;
    const isPresetImageStyle = imageStyle ? AiImagePresetStyles.some(style => style.style === imageStyle) : null;

    return {
        ..._getFilesProps(metadata.files ?? []),
        presentation_id: slide.presentationId,
        slide_id: slide.id,
        prompt: metadata.slidePrompt,
        language: metadata.language,
        used_ai_images: imageSource === "ai",
        model_name: imageModel,
        image_quality: imageQuality,
        custom_style_type: imageStyle ? (isPresetImageStyle ? "preset" : "prompt") : null,
        custom_style_type_prompt: imageStyle ? (isPresetImageStyle ? null : imageStyle) : null,
        image_style: imageStyle ? (isPresetImageStyle ? imageStyle : "custom") : null,
        context_source: metadata.files?.length ? "file" : undefined,
        use_presentation_as_context: metadata.presentationAsContext,
        preserve_exact_text: metadata.preserveExactText,
        web_search: metadata.webSearch,
        slide_type: metadata.slideType,
        template_id: slide.template_id
    } as Record<string, any>;
}

export function GenerateSlide({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    trackEvent(AmplitudeCategories.Slide, "Generate", props);
}

export function GenerateSlideIdeas({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    props.generated_ideas = _getSlide(_slide).metadata?.generatedIdeas?.map(({ slideCategory, slideConfidence, slideReasoning }) => ({ category: slideCategory, confidence: slideConfidence, reasoning: slideReasoning }));
    trackEvent(AmplitudeCategories.Slide, "GenerateIdeas", props);
}

export function SelectSlideIdea({ slide: _slide }: { slide: ISlide | IStorageModel<ISlide> }) {
    const props = _getSlideProps(_slide);
    props.selected_template_id = props.template_id;
    trackEvent(AmplitudeCategories.Slide, "SelectIdea", props);
}

export function RegenerateImagePrompt({ slide: _slide, slidePrompt, presentationTopic, generatedImagePrompt }: { slide: ISlide | IStorageModel<ISlide>, slidePrompt: string, presentationTopic: string, generatedImagePrompt: string }) {
    const props = _getSlideProps(_slide);
    props.slide_prompt = slidePrompt;
    props.presentation_topic = presentationTopic;
    props.generated_image_prompt = generatedImagePrompt;
    trackEvent(AmplitudeCategories.Image, "RegeneratePrompt", props);
}
